import React, { createContext, useContext, useEffect, useRef, useState } from "react";

/**
 * Global music player state — persistent across pages.
 *
 * State exposed:
 *   current, queue, index, isPlaying, shuffle, repeat, mode ("mini"|"expanded"|"floating"),
 *   favorites, recent
 *
 * Note: elapsed/duration on Spotify iframe is not reliably exposed cross-origin,
 * so the visual seek bar is presentational and drives seek only for users who
 * open the track on Spotify directly.
 */
const Ctx = createContext(null);
const KEY = "kx_player_v2";

const readStored = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "{}");
  } catch {
    return {};
  }
};

export const MusicPlayerProvider = ({ children }) => {
  const stored = useRef(readStored());
  const [queue, setQueue] = useState(stored.current.queue || []);
  const [index, setIndex] = useState(stored.current.index ?? -1);
  const [mode, setMode] = useState("expanded"); // expanded | mini | floating
  const [isPlaying, setIsPlaying] = useState(false);
  const [shuffle, setShuffle] = useState(stored.current.shuffle || false);
  const [repeat, setRepeat] = useState(stored.current.repeat || "off"); // off | all | one
  const [favorites, setFavorites] = useState(stored.current.favorites || []);
  const [recent, setRecent] = useState(stored.current.recent || []);
  const [queueDrawerOpen, setQueueDrawerOpen] = useState(false);

  const current = index >= 0 && index < queue.length ? queue[index] : null;
  const open = !!current;

  useEffect(() => {
    localStorage.setItem(
      KEY,
      JSON.stringify({ queue, index, shuffle, repeat, favorites, recent }),
    );
  }, [queue, index, shuffle, repeat, favorites, recent]);

  const play = (release, list) => {
    if (!release) return;
    const next = Array.isArray(list) && list.length ? list : [release];
    const nextIndex = Math.max(0, next.findIndex((x) => x.id === release.id));
    setQueue(next);
    setIndex(nextIndex);
    setIsPlaying(true);
    setMode((m) => (m === "floating" ? "expanded" : m));
    setRecent((prev) => {
      const filtered = prev.filter((r) => r.id !== release.id);
      return [{
        id: release.id, title: release.title, hindi_title: release.hindi_title, cover_image: release.cover_image,
      }, ...filtered].slice(0, 12);
    });
  };

  const playNow = (release) => play(release, [release]);

  const enqueue = (release) => {
    if (!release) return;
    setQueue((q) => {
      if (q.some((x) => x.id === release.id)) return q;
      return [...q, release];
    });
  };

  const next = () => {
    if (queue.length === 0) return;
    if (repeat === "one") {
      // reload same track by bumping index re-set
      setIndex((i) => i);
      return;
    }
    if (shuffle) {
      let ni = Math.floor(Math.random() * queue.length);
      if (queue.length > 1 && ni === index) ni = (ni + 1) % queue.length;
      setIndex(ni);
      return;
    }
    setIndex((i) => {
      if (i + 1 < queue.length) return i + 1;
      return repeat === "all" ? 0 : i;
    });
  };

  const prev = () => {
    if (queue.length === 0) return;
    setIndex((i) => (i - 1 >= 0 ? i - 1 : repeat === "all" ? queue.length - 1 : 0));
  };

  const close = () => {
    setIndex(-1);
    setQueue([]);
    setIsPlaying(false);
  };

  const toggleFavorite = (id) => {
    setFavorites((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const removeFromQueue = (id) => {
    setQueue((q) => {
      const removedIndex = q.findIndex((x) => x.id === id);
      if (removedIndex < 0) return q;
      const nq = q.filter((x) => x.id !== id);
      if (removedIndex < index) setIndex((i) => i - 1);
      if (removedIndex === index) setIndex((i) => Math.min(i, nq.length - 1));
      return nq;
    });
  };

  const jumpTo = (id) => {
    setIndex(queue.findIndex((x) => x.id === id));
  };

  const value = {
  current,
  queue,
  index,
  open,
  mode,
  setMode,
  isPlaying,
  setIsPlaying,
  shuffle,
  setShuffle,
  repeat,
  setRepeat,
  favorites,
  recent,
  play,
  playNow,
  enqueue,
  next,
  prev,
  close,
  toggleFavorite,
  removeFromQueue,
  jumpTo,
  queueDrawerOpen,
  setQueueDrawerOpen,
  };
  
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useMusic = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useMusic must be used within MusicPlayerProvider");
  return v;
};
